import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ExternalLink, RefreshCw, Server } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, EmptyState, Field } from "@/components/ranger/PageHeader";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const STATUS = { pending: "text-amber-400", active: "text-emerald-400", rejected: "text-red-400" };
const STEPS = ["Discord server", "Community", "RCON"];
const EMPTY = { guild_id: "", name: "", mode: "pvp", host: "", port: "", password: "" };

const Mine = ({ rows }) => (
  <Card title="Your servers" icon={Server} testId="setup-mine-card" className="mb-5">
    {rows.length === 0 ? <p className="text-sm text-slate-400">No applications yet.</p> : (
      <ul className="divide-y divide-[#1E2D4A]">{rows.map((r) => (
        <li key={r.id} data-testid={`setup-mine-${r.id}`} className="flex items-center justify-between py-2 text-sm">
          <span className="text-slate-200">{r.name} <span className="text-xs text-slate-500">· {fmtTime(r.created_at)}</span></span>
          <span data-testid={`setup-mine-status-${r.id}`} className={`font-mono text-xs uppercase ${STATUS[r.status]}`}>{r.status === "pending" ? "Waiting for approval" : r.status}{r.review_note ? ` — ${r.review_note}` : ""}</span>
        </li>))}</ul>)}
  </Card>
);

const GuildStep = ({ g, v, set, reload }) => (
  <div className="space-y-3">
    <p className="text-sm text-slate-400">Add the Ranger bot to your Discord server, then pick it. Only the server owner can add a server.</p>
    <div className="flex flex-wrap gap-2">
      {g.invite_url && <a data-testid="setup-invite-bot" href={g.invite_url} target="_blank" rel="noreferrer" className={BTN_SECONDARY}><ExternalLink className="h-4 w-4" />Invite Ranger bot</a>}
      <button type="button" data-testid="setup-refresh-guilds" onClick={reload} className={BTN_SECONDARY}><RefreshCw className="h-4 w-4" />Refresh</button>
    </div>
    {g.guilds.length === 0 ? <p data-testid="setup-no-guilds" className="text-sm text-amber-400">No Discord servers found that you own with the bot in them.</p> : (
      <div className="grid gap-2 sm:grid-cols-2">{g.guilds.map((x) => (
        <button type="button" key={x.id} data-testid={`setup-guild-${x.id}`} onClick={() => set({ ...v, guild_id: x.id })}
          className={`rounded-sm border px-3 py-2 text-left text-sm transition-colors ${v.guild_id === x.id ? "border-[#E5202E] bg-[#E5202E]/10 text-white" : "border-[#1E2D4A] text-slate-300 hover:border-slate-500"}`}>
          {x.name}<span className="block text-xs text-slate-500">{x.member_count ?? "?"} members</span></button>))}</div>)}
  </div>
);

const CommunityStep = ({ v, set }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <Field label="Community name"><Input data-testid="setup-name-input" maxLength={60} value={v.name} onChange={(e) => set({ ...v, name: e.target.value })} /></Field>
    <Field label="Game mode"><select data-testid="setup-mode-select" value={v.mode} onChange={(e) => set({ ...v, mode: e.target.value })} className="h-9 w-full rounded-sm border border-[#1E2D4A] bg-[#0B1120] px-2 text-sm text-slate-200"><option value="pvp">PvP</option><option value="pve_purge">PvE + monthly purge</option></select></Field>
  </div>
);

const RconStep = ({ v, set }) => (
  <div className="grid gap-4 sm:grid-cols-3">
    <Field label="RCON IP / host" hint="From your G-Portal server page"><Input data-testid="setup-host-input" value={v.host} onChange={(e) => set({ ...v, host: e.target.value })} /></Field>
    <Field label="RCON port"><Input data-testid="setup-port-input" type="number" value={v.port} onChange={(e) => set({ ...v, port: e.target.value })} /></Field>
    <Field label="RCON password" hint="Stored encrypted"><Input data-testid="setup-password-input" type="password" value={v.password} onChange={(e) => set({ ...v, password: e.target.value })} /></Field>
  </div>
);

const ready = (step, v) => [!!v.guild_id, v.name.trim().length >= 2, v.host && v.port && v.password][step];

export default function Setup() {
  const { session } = useAuth();
  const [g, setG] = useState({ guilds: [] });
  const [mine, setMine] = useState([]);
  const [v, setV] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const loadG = useCallback(() => api.get("/signup/guilds").then((r) => setG(r.data)).catch((e) => toast.error(errMsg(e))), []);
  const loadMine = useCallback(() => api.get("/signup/mine").then((r) => setMine(r.data)).catch(() => {}), []);
  useEffect(() => { if (session?.user?.discord_id) { loadG(); loadMine(); } }, [session, loadG, loadMine]);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/signup/communities", { ...v, port: Number(v.port) }); toast.success("Sent for approval. You'll get a Discord DM once it's reviewed."); setV(EMPTY); setStep(0); loadMine(); loadG(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const Body = [GuildStep, CommunityStep, RconStep][step];
  return (
    <div className="min-h-screen bg-[#050811] rg-grid-bg">
      <header className="flex items-center justify-between border-b border-[#1E2D4A] bg-[#070A14] px-6 py-3">
        <div className="flex items-center gap-3"><img src="/ranger-logo.jpg" alt="RANGER" className="h-9 w-9 rounded-sm" /><span className="font-display text-xl font-bold uppercase tracking-[0.25em] text-slate-100">Ranger</span><span className="rg-label">Add your server</span></div>
        <Link data-testid="setup-back-link" to="/portal" className="flex items-center gap-1 text-xs text-slate-300 hover:text-white"><ArrowLeft className="h-3 w-3" />Player portal</Link>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8">
        {!session?.user?.discord_id ? <EmptyState title="Discord sign-in required" testId="setup-needs-discord">Sign in with Discord to add your server.</EmptyState> : (<>
          <Mine rows={mine} />
          <Card title={`Step ${step + 1} of 3 · ${STEPS[step]}`} icon={CheckCircle2} testId="setup-wizard-card">
            <ol className="mb-5 flex gap-2">{STEPS.map((s, i) => <li key={s} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-[#E5202E]" : "bg-[#1E2D4A]"}`} />)}</ol>
            <Body g={g} v={v} set={setV} reload={loadG} />
            <div className="mt-6 flex justify-between">
              <button type="button" data-testid="setup-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)} className={BTN_SECONDARY}>Back</button>
              {step < 2 ? <button type="button" data-testid="setup-next-btn" disabled={!ready(step, v)} onClick={() => setStep(step + 1)} className={BTN_PRIMARY}>Next</button>
                : <button type="button" data-testid="setup-submit-btn" disabled={!ready(2, v) || busy} onClick={submit} className={BTN_PRIMARY}>{busy ? "Sending…" : "Send for approval"}</button>}
            </div>
            <p className="mt-4 text-xs text-slate-500">Each Discord account can add up to 3 servers. New servers go live after RANGER staff approve them.</p>
          </Card></>)}
      </main>
    </div>
  );
}
